import {
  AccountData,
  DMAutomation,
  FollowerStatus,
} from "@markit/common.types";
import filter from "lodash.filter";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../../redux/slices/accountSlice";
import { Colors } from "../../../../../utils/colors";
import { Icon } from "@iconify/react";
import {
  addOrFilterRecipients,
  filterFollowersByStatus,
  GetDateWithTime,
  uniqueVals,
} from "@markit/common.utils";
import { MassTextsPersonItem } from "../../Items/MassTextsPersonItem";
import { MassTextsActionItem } from "../../Items/MassTextsActionItem";
import { EmptySearchState } from "../../../../EmptyStates/EmptySearchState";
import SearchBoxContainer from "../../../../Containers/SearchBoxContainer";
import VirtuosoList from "../../../../Lists/VirtuosoList";
import { SelectRecipientsSharedProps } from "../SelectRecipientsScreens";
import useAsyncOnMount from "../../../../../hooks/useAsyncEffectOnMount";
import { getUserDmAutomationSubmissionsSnap } from "../../../../../utils/FirebaseUtils";
import { useLoadDMAutomationSubmissions } from "../../../../../hooks/useLoadDMAutomationSubmissions";
import { HorizontalDivider } from "../../../../Dividers/HorizontalDivider";
import SocialProfilePicsLabel from "../../../../DisplayItem/AutoDM/SocialProfilePicsLabel";
import { SocialPlatformIconItem } from "../../../../DisplayItem/AutoDM/SocialPlatformIconItem";

type SelectRecipientsAutoDMProps = SelectRecipientsSharedProps & {
  selectedAutomation: DMAutomation;
};

const SelectRecipientsAutoDM = (props: SelectRecipientsAutoDMProps) => {
  const {
    unsavedRecipients,
    setUnsavedRecipients,
    selectedAutomation,
    excludingMode,
    setProfileSelected,
    displayAllFollowers,
  } = props;
  const { accountData, followersData } = useSelector(getAccountState).account;

  const [searchTerm, setSearchTerm] = useState("");
  const [allSubmissionUids, setAllSubmissionUids] = useState<string[]>([]);

  const iconColor = useMemo(
    () => (excludingMode ? Colors.RED3 : Colors.BLUE5),
    [excludingMode]
  );

  const {
    fetchedUserData,
    isLoading,
    isFinished,
    loadSubmissions,
    loadMoreSubmissions,
    loadSearchResultsThrottled,
  } = useLoadDMAutomationSubmissions({
    creatorUid: accountData.uid,
    dmAutomationId: selectedAutomation.id,
    windowSize: 15,
  });

  useAsyncOnMount(async () => {
    await loadSubmissions();
    const snap = await getUserDmAutomationSubmissionsSnap(
      accountData.uid,
      selectedAutomation.id
    );
    setAllSubmissionUids(uniqueVals(snap.docs.map((doc) => doc.data().uid)));
  });

  const containsUser = useCallback((item: AccountData, query: string) => {
    return item.fullName.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    if (text !== "") {
      loadSearchResultsThrottled(text.toLowerCase());
    }
    setSearchTerm(text);
  };

  const visibleSubmissionUids = useMemo(
    () =>
      displayAllFollowers
        ? allSubmissionUids
        : filterFollowersByStatus(
            allSubmissionUids,
            followersData,
            FollowerStatus.SUBSCRIBED
          ),
    [displayAllFollowers, allSubmissionUids, followersData]
  );

  const usersToShow: AccountData[] = useMemo(() => {
    let subscribedFiltered = fetchedUserData;
    if (!displayAllFollowers) {
      const subscribedUids = new Set(
        filterFollowersByStatus(
          fetchedUserData.map((user) => user.uid),
          followersData,
          FollowerStatus.SUBSCRIBED
        )
      );
      subscribedFiltered = filter(fetchedUserData, (user: AccountData) =>
        subscribedUids.has(user.uid)
      );
    }
    if (searchTerm === "") {
      return subscribedFiltered;
    }
    return filter(subscribedFiltered, (user: AccountData) =>
      containsUser(user, searchTerm)
    );
  }, [
    fetchedUserData,
    displayAllFollowers,
    followersData,
    searchTerm,
    containsUser,
  ]);

  const isAllSelected = useMemo(
    () =>
      visibleSubmissionUids.length > 0 &&
      visibleSubmissionUids.every((uid) => unsavedRecipients.includes(uid)),
    [visibleSubmissionUids, unsavedRecipients]
  );

  const selectAllOnPress = useCallback(() => {
    addOrFilterRecipients(
      unsavedRecipients,
      setUnsavedRecipients,
      visibleSubmissionUids
    );
  }, [visibleSubmissionUids, setUnsavedRecipients, unsavedRecipients]);

  const renderHeader = useMemo(
    () => (
      <div>
        <MassTextsActionItem
          title={"Select All"}
          icon={
            <Icon icon="ion:people" height={32} style={{ color: iconColor }} />
          }
          onPress={selectAllOnPress}
          subtext={`${visibleSubmissionUids.length} Textable Contact${
            visibleSubmissionUids.length !== 1 ? "s" : ""
          }`}
          isCheckSelected={isAllSelected}
          checkColor={iconColor}
        />
        <HorizontalDivider altMargin={10} />
      </div>
    ),
    [visibleSubmissionUids.length, iconColor, isAllSelected, selectAllOnPress]
  );

  return (
    <>
      <div
        className="AlignedRow"
        style={{
          backgroundColor: Colors.GRAY6,
          padding: 10,
          borderRadius: 12,
          gap: 10,
        }}
      >
        <SocialPlatformIconItem
          platform={selectedAutomation.platform}
          size={32}
          noBorder
        />
        <div className="ColumnNormal" style={{ gap: 4 }}>
          <span className="bodyMedium">{selectedAutomation.title}</span>
          <div className="AlignedRow" style={{ gap: 4 }}>
            <SocialProfilePicsLabel
              igAccountIds={selectedAutomation.igAccountIds}
              platform={selectedAutomation.platform}
            />
          </div>
          <span className="smallBodySubtext">
            Created: {GetDateWithTime(selectedAutomation.createdAt, true)}
          </span>
        </div>
      </div>
      <SearchBoxContainer
        value={searchTerm}
        placeholder="Search People"
        onChange={(e) => handleSearch(e.target.value)}
        containerStyles={{ marginBottom: 14 }}
      />
      <VirtuosoList
        items={usersToShow}
        loadMoreOnPress={loadMoreSubmissions}
        renderItem={(item) => (
          <MassTextsPersonItem
            user={item}
            unsavedPeople={unsavedRecipients}
            setUnsavedPeople={setUnsavedRecipients}
            excludingMode={excludingMode}
            setProfileSelected={setProfileSelected}
          />
        )}
        isLoading={isLoading}
        isFinished={isFinished}
        searchTerm={searchTerm}
        renderNonSearchEmptyView={
          <EmptySearchState mainText="No submissions to show" />
        }
        renderHeader={renderHeader}
      />
    </>
  );
};

export default SelectRecipientsAutoDM;
